import { useCallback, useEffect, useMemo, useState } from 'react';
import { UnifiedTable } from '../../../../components/UnifiedTable';
import { useApi } from '../../../../hooks/useApi';
import OzonFbsStocksTable from './OzonFbsStocksTable';

function formatDt(v) {
    if (!v) return '—';
    const d = new Date(v);
    return isNaN(d) ? String(v) : d.toLocaleString('ru-RU');
}

const WAREHOUSE_STATUS_LABELS = {
    new: 'Новый',
    created: 'Активен',
    enabled: 'Активен',
    rating: 'На проверке',
    disabled: 'Отключён',
    disabled_due_to_limit: 'Отключён (лимит)',
    blocked: 'Заблокирован',
    error: 'Ошибка',
};

const SETTINGS_TABS = [
    { key: 'warehouses', label: 'Склады' },
    { key: 'stocks', label: 'Остатки FBS' },
];

function OzonFbsSettings({ accountId }) {
    const { request, showStatus } = useApi();
    const [tab, setTab] = useState('warehouses');
    const [busy, setBusy] = useState('');
    const [warehouses, setWarehouses] = useState([]);

    const loadWarehouses = useCallback(async () => {
        const data = await request(`/api/v1/ozon-ops/${accountId}/fbs/warehouses`);
        setWarehouses(Array.isArray(data) ? data : []);
    }, [accountId, request]);

    const syncWarehouses = useCallback(async () => {
        setBusy('warehouses');
        try {
            await request(`/api/v1/ozon-ops/${accountId}/fbs/warehouses/sync`, { method: 'POST' });
            await loadWarehouses();
            return true;
        } catch (e) {
            showStatus('error', e?.message || 'Ошибка синхронизации складов.');
            return false;
        } finally {
            setBusy('');
        }
    }, [accountId, request, showStatus, loadWarehouses]);

    // Синхронизация происходит автоматически при заходе на вкладку — без ручного клика.
    // Сначала показываем то, что уже есть в БД (быстро), затем обновляем в фоне.
    useEffect(() => {
        if (!accountId) return;
        loadWarehouses();
        syncWarehouses();
        // eslint-disable-next-line
    }, [accountId]);

    const warehouseColumns = useMemo(() => [
        { key: 'name', header: 'Склад', width: 240, type: 'text', sortable: false },
        { key: 'warehouse_id', header: 'ID', width: 140, type: 'text', sortable: false },
        { key: 'status', header: 'Статус', width: 140, type: 'text', sortable: false },
        { key: 'synced_text', header: 'Синхронизация', width: 180, type: 'text', sortable: false },
    ], []);

    const warehouseData = useMemo(() => warehouses
        .filter((w) => (w.status || '').toLowerCase() !== 'disabled')
        .map(w => ({
            ...w,
            name: w.name || '—',
            status: WAREHOUSE_STATUS_LABELS[(w.status || '').toLowerCase()] || w.status || '—',
            synced_text: formatDt(w.synced_at),
        })), [warehouses]);

    return (
        <div className="stack-12">
            <div className="account-tabs account-tabs--secondary" style={{ marginBottom: 0 }}>
                {SETTINGS_TABS.map(t => (
                    <button key={t.key} type="button"
                        className={tab === t.key ? 'active' : ''}
                        onClick={() => setTab(t.key)}>
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === 'warehouses' && (
                <div className="stack-8">
                    <p className="muted" style={{ margin: 0, fontSize: 13 }}>
                        Справочник складов FBS для этого кабинета (только для чтения).
                    </p>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button" disabled={busy === 'warehouses'}
                            onClick={async () => { if (await syncWarehouses()) showStatus('success', 'Склады обновлены.'); }}>
                            {busy === 'warehouses' ? 'Синхронизация…' : 'Синхронизировать склады'}
                        </button>
                    </div>
                    <UnifiedTable
                        tableId="ozon-fbs-warehouses"
                        columns={warehouseColumns}
                        data={warehouseData}
                        totalRows={warehouseData.length}
                        showPagination={false}
                        onCopyTable={true}
                        getRowKey={(row) => row.id || row.warehouse_id}
                    />
                </div>
            )}

            {tab === 'stocks' && <OzonFbsStocksTable accountId={accountId} />}
        </div>
    );
}

export default OzonFbsSettings;
